// src/api/http.js
import axios from "axios";

/* ========= Environment =========
 * Works in CRA/Webpack and Vite. Also allows a window override.
 */
const ENV = (typeof process !== "undefined" && process.env) ? process.env : {};
const API_BASE = (
  ENV.REACT_APP_API_BASE ||
  ENV.VITE_API_BASE ||
  ENV.API_BASE ||
  (typeof window !== "undefined" && window.__API_BASE__) ||
  "https://api.peraeng.com"
).replace(/\/+$/, "");

const ASSET_BASE = (
  ENV.REACT_APP_ASSET_BASE ||
  ENV.VITE_ASSET_BASE ||
  ENV.ASSET_BASE ||
  (typeof window !== "undefined" && window.__ASSET_BASE__) ||
  API_BASE
).replace(/\/+$/, "");

const WITH_CREDENTIALS = String(
  ENV.REACT_APP_API_CREDENTIALS || ENV.VITE_API_CREDENTIALS || "false"
).toLowerCase() === "true";

/* ========= Axios instance ========= */
const api = axios.create({
  baseURL: API_BASE,                 // NOTE: no '/api' here
  withCredentials: WITH_CREDENTIALS, // cookie auth toggle via env
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

/* ========= Helpers ========= */

// Normalize an API path (relative or absolute). Do NOT add '/api' here.
export const apiPath = (p = "") => {
  if (!p) return "/";
  if (/^https?:\/\//i.test(p)) return p;           // absolute: leave as-is
  return p.startsWith("/") ? p : `/${p}`;          // ensure leading slash
};

// Smart absolute URL builder for images/files (Laravel public storage aware)
export const absUrl = (input) => {
  if (!input) return "";
  let p = String(input).trim();
  if (!p) return "";

  // Absolute URL? return as-is.
  if (/^https?:\/\//i.test(p)) return p;

  // Normalize slashes, strip accidental prefixes
  p = p.replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  p = p.replace(/^public\//i, "");                    // 'public/...'
  p = p.replace(/^storage\/app\/public\/?/i, "");     // 'storage/app/public/...'

  // Ensure leading slash
  if (!p.startsWith("/")) p = `/${p}`;

  // React public assets (kept as path so dev server serves them)
  if (/^\/(images|assets)\//i.test(p)) return p;

  // If not already /storage, but looks like an upload path, prefix /storage
  if (!/^\/storage\//i.test(p)) {
    const test = p.replace(/^\//, ""); // drop leading slash for test
    if (/^(uploads|projects|files|media|gallery)\//i.test(test)) {
      p = `/storage/${test}`;
    }
  }

  return `${ASSET_BASE}${p}`;
};

// Optional: set/remove Authorization header (Bearer)
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
};

// Optional: fetch Sanctum CSRF cookie (only useful if WITH_CREDENTIALS=true)
export const fetchCsrfCookie = async () => {
  if (!WITH_CREDENTIALS) return;
  try {
    await api.get("/sanctum/csrf-cookie"); // interceptor will not prefix this
  } catch {
    // swallow; caller can decide what to do
  }
};

/* ========= Interceptors ========= */

// Prefix '/api' for relative URLs; leave absolute URLs untouched
api.interceptors.request.use((cfg) => {
  let url = cfg.url || "";

  // Absolute? leave as-is
  if (/^https?:\/\//i.test(url)) return cfg;

  // Ensure leading slash
  if (!url.startsWith("/")) url = `/${url}`;

  // Leave Sanctum CSRF endpoint untouched
  const isCsrf = url === "/sanctum/csrf-cookie";

  // Normalize accidental '/api/api/...'
  url = url.replace(/^\/api\/api(\/|$)/, "/api$1");

  // If not csrf and not already under '/api', prefix it
  if (!isCsrf && !url.startsWith("/api/")) url = `/api${url}`;

  cfg.url = url;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

export default api;